import { CheckCircle, ChevronLeft, ChevronRight, MessageSquare, X } from "lucide-react";
import { TourScreenProps } from "../types";
import { AIAssistant } from "../../../../components/marketing/cpas-platform/ai-asisstant";

export const TourScreen: React.FC<TourScreenProps> = ({
  step,
  currentStep,
  totalSteps,
  onNext,
  onPrevious,
  onClose,
  assistantMessage,
}) => {
  const isLastStep = currentStep === totalSteps;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Tour Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Product Tour</h3>
                <p className="text-sm text-gray-600">Step {currentStep} of {totalSteps}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Skip Tour
              <X className="w-4 h-4" />
            </button>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4 w-full bg-gray-200 rounded-full h-1.5">
            <div
              className="bg-indigo-600 h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Tour Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left: AI Assistant */}
          <div className="flex justify-center lg:justify-start">
            <AIAssistant message={assistantMessage} showActions={true} />
          </div>

          {/* Right: Feature Card */}
          <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm">
            <div className="w-16 h-16 bg-indigo-600 rounded-2xl flex items-center justify-center mb-6">
              {step.icon}
            </div>
            
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{step.title}</h2>
            <p className="text-gray-600 mb-8 text-lg leading-relaxed">{step.description}</p>
            
            <div className="mb-8">
              <p className="text-sm font-semibold text-gray-900 mb-4">Key Features:</p>
              <ul className="space-y-3">
                {step.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between pt-6 border-t border-gray-200">
              <button
                onClick={onPrevious}
                disabled={currentStep === 1}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg transition-colors font-medium ${
                  currentStep === 1
                    ? 'text-[#7B7B7B] font-semibold cursor-not-allowed'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
              
              <button
                onClick={onNext}
                className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
              >
                {isLastStep ? 'Get Started' : 'Next'}
                {!isLastStep && <ChevronRight className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
